import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { useProfile } from '@/auth/SessionProvider'
import { listDeletions, pendingFileRemovals, retryFileRemovals, summariseCounts } from '@/data/ownerDelete'
import { formatDay } from '@/lib/format'
import { lusakaDay } from '@/lib/periods'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** Everything the owner has ever permanently deleted, who did it, when and why. Administrators and auditors can read it. */
export function DeletionsPage() {
  const profile = useProfile()
  const qc = useQueryClient()
  const log = useQuery({ queryKey: ['owner-deletions'], queryFn: listDeletions })
  const pending = useQuery({ queryKey: ['owner-pending-files'], queryFn: pendingFileRemovals, enabled: profile.isOwner })
  const [error, setError] = useState('')
  const retry = useMutation({ mutationFn: retryFileRemovals })

  const removeNow = async () => {
    setError('')
    try {
      await retry.mutateAsync()
      await qc.invalidateQueries({ queryKey: ['owner-pending-files'] })
    } catch (e) {
      setError(errText(e))
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Deletion log</h2>
        <p className="mt-1 text-muted">
          Records that were permanently deleted by the owner of the app. Nothing here can be changed or removed. The books themselves no longer show what was deleted, so this is the record that it happened.
        </p>
      </div>

      {profile.isOwner && (pending.data?.length ?? 0) > 0 && (
        <Card>
          <CardBody>
            <p role="status" className="text-ink">
              {pending.data!.length} stored {pending.data!.length === 1 ? 'photo' : 'photos'} from deleted records {pending.data!.length === 1 ? 'is' : 'are'} still waiting to be removed.
            </p>
            {error && <p role="alert" className="mt-2 text-body text-bad">{error}</p>}
            <Button size="lg" className="mt-3" isLoading={retry.isPending} onClick={() => void removeNow()}>Remove them now</Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader><h3 className="font-semibold text-ink">Deleted records</h3></CardHeader>
        <CardBody>
          {log.isPending ? (
            <SkeletonRows rows={4} />
          ) : log.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(log.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void log.refetch()}>Try again</Button>
            </div>
          ) : (log.data ?? []).length === 0 ? (
            <EmptyState title="Nothing has been deleted" description="When the owner permanently deletes a record, it is listed here." />
          ) : (
            <ul className="divide-y divide-line" aria-label="Deleted records">
              {(log.data ?? []).map((d) => (
                <li key={d.id} className="py-3">
                  <p className="font-medium text-ink">{d.label}</p>
                  <p className="text-body text-muted">Deleted by {d.deletedBy} on {formatDay(lusakaDay(d.deletedAt))}</p>
                  <p className="mt-1 text-body text-ink">Reason: {d.reason}</p>
                  {summariseCounts(d.deleted) && <p className="text-body text-muted">Removed: {summariseCounts(d.deleted)}</p>}
                  {d.warnings.map((w) => <p key={w} className="text-body text-wait">Note: {w}</p>)}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
